import React, { useEffect, useState, useRef, useMemo } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError, API, authQuery } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plus, Trash2, X, Award, Save, ScrollText } from "lucide-react";
import FileInputLocalized from "../components/FileInputLocalized";

// All variables the back-end can fill in. The admin picks one per overlay box.
// Labels resolve via t("diplomas.field_<key>") so they auto-localize.
const FIELD_KEYS = [
  { key: "member_name" },
  { key: "first_name" },
  { key: "last_name" },
  { key: "grade" },
  { key: "degree" },
  { key: "date" },
  { key: "date_initiated" },
  { key: "date_promoted" },
  { key: "lodge_name" },
  { key: "lodge_number" },
  { key: "obedience" },
  { key: "city" },
  { key: "country" },
  // Symbolic lodge officers (rendered as the officer's full name)
  { key: "venerabil_maestru" },
  { key: "secretar" },
  { key: "trezorierul" },
  { key: "prim_supraveghetor" },
  { key: "al_doilea_supraveghetor" },
  // Grand-Lodge officers (rendered as the officer's full name)
  { key: "mare_maestru" },
  { key: "mare_secretar" },
  { key: "mare_trezorier" },
  { key: "mare_cancelar" },
  { key: "mare_prim_supraveghetor" },
  { key: "mare_al_doilea_supraveghetor" },
  { key: "masonic_year" },
  { key: "anno_lucis" },
  { key: "series" },
  { key: "__custom" },
];

function emptyField(key = "member_name", type = "text") {
  return {
    type,                // "text" | "stamp" | "signature" | "photo"
    key,                 // data binding (only for text fields)
    source: type === "signature" ? "Maestru Venerabil" : null,
    x: 0.5, y: 0.5, width: 0.4,
    font_size: type === "text" ? 22 : 110,  // points; ~4cm for image fields
    font: "regular",
    color: "#0d1b2a",
    align: "center",
    default_value: "",
  };
}

const FIELD_TYPES = [
  { type: "text" },
  { type: "stamp" },
  { type: "signature" },
  { type: "photo" },
];

// Complete officer list for the diploma "signature" field selector.
// Aligned with the canonical lodge-office enum used across MemberProfile,
// UsersPage, and LodgeInfo. Includes BOTH the 9 symbolic lodge offices AND
// the 6 Grand-Lodge officers — so a sub-lodge admin sees the symbolic set
// and a Grand Lodge admin can wire up Mare Maestru / Mare Trezorier etc.
// Stored value is the canonical Romanian key (the i18n layer translates
// the visible label per UI language via memberProfile.lodgeOffice.*).
const OFFICER_ROLES = [
  // Symbolic lodge officers
  "Maestru Venerabil",
  "Prim Supraveghetor",
  "Al Doilea Supraveghetor",
  "Secretar",
  "Trezorier",
  "Orator",
  "Maestru de Ceremonii",
  "Maestru de Armonie",
  "Acoperitor",
  // Grand Lodge officers
  "Mare Maestru",
  "Mare Prim Supraveghetor",
  "Mare al Doilea Supraveghetor",
  "Mare Secretar",
  "Mare Trezorier",
  "Mare Cancelar",
];

export default function DiplomasPage() {
  const { t, i18n } = useTranslation();
  const en = i18n.language !== "ro";
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [templates, setTemplates] = useState([]);
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null); // template currently being edited
  const [newDialog, setNewDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateDialog, setGenerateDialog] = useState(false);

  // New template upload form
  const [newName, setNewName] = useState("");
  const [newLang, setNewLang] = useState("ro");
  const [newFile, setNewFile] = useState(null);

  // Generation form: single dict {lang: templateId} to support all 7
  // UI languages (ro/en/fr/it/es/de/pt). Empty string means "skip this
  // language". The backend accepts a `template_ids` object mapping ISO
  // 639-1 → template id and generates one PDF per non-empty slot.
  const [genMemberId, setGenMemberId] = useState("");
  const [genTplIds, setGenTplIds] = useState({});
  const [genIssueDate, setGenIssueDate] = useState(() => new Date().toISOString().slice(0, 10));

  const load = async () => {
    try {
      const { data } = await api.get("/diplomas/templates");
      setTemplates(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
    api.get("/members").then(({ data }) => setMembers(data || [])).catch(() => {});
  }, [isAdmin]);

  const createTemplate = async (e) => {
    e.preventDefault();
    if (!newFile) {
      toast.error(t("diplomas.choose_a_template_image"));
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", newFile);
      fd.append("name", newName);
      fd.append("language", newLang);
      const { data } = await api.post("/diplomas/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("diplomas.template_uploaded"));
      setNewDialog(false);
      setNewFile(null); setNewName(""); setNewLang("ro");
      await load();
      setEditing(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeTemplate = async (tpl) => {
    if (!window.confirm(en ? `Delete template "${tpl.name}"?` : `Ștergi șablonul "${tpl.name}"?`)) return;
    try {
      await api.delete(`/diplomas/templates/${tpl.id}`);
      if (editing?.id === tpl.id) setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const generateDiplomas = async () => {
    // Retain only non-empty (lang → templateId) pairs from the state.
    const template_ids = Object.fromEntries(
      Object.entries(genTplIds).filter(([, id]) => !!id)
    );
    if (!genMemberId || Object.keys(template_ids).length === 0) {
      toast.error(t("diplomas.pick_a_member_and_at"));
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/diplomas/generate", {
        member_id: genMemberId,
        template_ids,
        issue_date: genIssueDate || null,
      });
      toast.success(t("diplomas.generated_count", { count: data.results.length }));
      setGenerateDialog(false);
      setGenMemberId("");
      setGenTplIds({});
      setGenIssueDate(new Date().toISOString().slice(0, 10));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  if (!isAdmin) {
    return (
      <div data-testid="diplomas-page">
        <h1 className="page-title">{t("diplomas.diplomas")}</h1>
        <p className="page-subtitle">{t("diplomas.admin_access_required")}</p>
      </div>
    );
  }

  return (
    <div data-testid="diplomas-page">
      <div className="page-header" style={{ marginBottom: "1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 className="page-title">{t("diplomas.diplomas")}</h1>
          <p className="page-subtitle">
            {t("diplomas.upload_graphic_templates_without_text")}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn-outline-gold flex items-center gap-2" onClick={() => setGenerateDialog(true)} data-testid="diploma-generate-btn">
            <Award size={14} /> {t("diplomas.generate_diploma")}
          </button>
          <button className="btn-primary-gold flex items-center gap-2" onClick={() => setNewDialog(true)} data-testid="diploma-new-template-btn">
            <Plus size={14} /> {t("diplomas.new_template")}
          </button>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="gold-card" style={{ padding: "2rem", textAlign: "center", color: "#9a7530", fontStyle: "italic" }} data-testid="diplomas-empty">
          <ScrollText size={32} color="#c9a84c" style={{ margin: "0 auto 0.6rem", display: "block" }} />
          {t("diplomas.no_templates_uploaded_yet")}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {templates.map((tpl) => (
            <div key={tpl.id} className="gold-card" style={{ padding: "1rem", display: "flex", flexDirection: "column", gap: 8 }} data-testid={`diploma-template-${tpl.id}`}>
              <div style={{ aspectRatio: "1.4/1", overflow: "hidden", border: "1px solid #ead8a8", background: "#fdfcf6" }}>
                <img
                  src={`${API}/diplomas/templates/${tpl.id}/image${authQuery()}`}
                  alt={tpl.name}
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </div>
              <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a" }}>
                {tpl.name} <span className="kv-label" style={{ marginLeft: 6, fontSize: "0.7rem" }}>· {tpl.language?.toUpperCase()}</span>
              </div>
              <div className="kv-label" style={{ fontSize: "0.7rem" }}>
                {(tpl.fields || []).length} {t("diplomas.fields_configured")}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button className="btn-outline-gold" style={{ flex: 1 }} onClick={() => setEditing(tpl)} data-testid={`diploma-edit-${tpl.id}`}>
                  {t("diplomas.configure")}
                </button>
                <button className="btn-danger" onClick={() => removeTemplate(tpl)} data-testid={`diploma-delete-${tpl.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {newDialog && (
        <div className="modal-overlay" onClick={() => setNewDialog(false)}>
          <div className="modal-content gold-card" style={{ maxWidth: 480, padding: "1.5rem" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em" }}>
                {t("diplomas.new_template")}
              </h2>
              <button onClick={() => setNewDialog(false)} className="text-gray-500"><X size={18} /></button>
            </div>
            <form onSubmit={createTemplate} className="space-y-3" data-testid="diploma-template-form">
              <div>
                <label className="label-lodge">{t("diplomas.name")}</label>
                <input className="input-lodge" required value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="diploma-template-name" />
              </div>
              <div>
                <label className="label-lodge">{t("diplomas.language")}</label>
                <select className="input-lodge" value={newLang} onChange={(e) => setNewLang(e.target.value)} data-testid="diploma-template-lang">
                  <option value="ro">Română</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                  <option value="it">Italiano</option>
                  <option value="es">Español</option>
                  <option value="de">Deutsch</option>
                  <option value="pt">Português</option>
                </select>
              </div>
              <div>
                <label className="label-lodge">{t("diplomas.image_png_jpg_webp")}</label>
                <FileInputLocalized
                  accept="image/png,image/jpeg,image/webp"
                  required
                  onChange={(e) => setNewFile(e.target.files?.[0] || null)}
                  testId="diploma-template-file"
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" className="btn-outline-gold" onClick={() => setNewDialog(false)}>{t("diplomas.cancel")}</button>
                <button type="submit" className="btn-primary-gold" data-testid="diploma-template-submit">{t("diplomas.upload")}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {generateDialog && (
        <div className="modal-overlay" onClick={() => setGenerateDialog(false)}>
          <div className="modal-content gold-card" style={{ maxWidth: 520, padding: "1.5rem" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em" }}>
                {t("diplomas.generate_diploma")}
              </h2>
              <button onClick={() => setGenerateDialog(false)} className="text-gray-500"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label-lodge">{t("diplomas.member")}</label>
                <select className="input-lodge" value={genMemberId} onChange={(e) => setGenMemberId(e.target.value)} data-testid="diploma-gen-member">
                  <option value="">{t("diplomas.select_member")}</option>
                  {[...members].sort((a, b) => `${a.last_name}`.localeCompare(b.last_name)).map((m) => (
                    <option key={m.id} value={m.id}>{m.last_name} {m.first_name} · {m.grade}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-lodge">{t("diplomas.issue_date")}</label>
                <input
                  type="date"
                  className="input-lodge"
                  value={genIssueDate}
                  onChange={(e) => setGenIssueDate(e.target.value)}
                  data-testid="diploma-gen-issue-date"
                />
                <p className="kv-label" style={{ fontSize: "0.66rem", marginTop: 4 }}>
                  {t("diplomas.this_date_will_be_printed")}
                </p>
              </div>
              {/* One template picker per language that has at least one
                  uploaded template. Admin can leave any language blank
                  to skip it — the payload only sends filled slots. */}
              {[
                { code: "ro", label: "Română" },
                { code: "en", label: "English" },
                { code: "fr", label: "Français" },
                { code: "it", label: "Italiano" },
                { code: "es", label: "Español" },
                { code: "de", label: "Deutsch" },
                { code: "pt", label: "Português" },
              ]
                .filter((lang) => templates.some((tpl) => tpl.language === lang.code))
                .map((lang) => (
                  <div key={lang.code}>
                    <label className="label-lodge">
                      {lang.code === "en"
                        ? t("diplomas.international_template")
                        : t("diplomas.national_template_lang", { lang: lang.label })}
                    </label>
                    <select
                      className="input-lodge"
                      value={genTplIds[lang.code] || ""}
                      onChange={(e) => setGenTplIds((prev) => ({ ...prev, [lang.code]: e.target.value }))}
                      data-testid={`diploma-gen-tpl-${lang.code}`}
                    >
                      <option value="">{t("diplomas.skip")}</option>
                      {templates
                        .filter((tpl) => tpl.language === lang.code)
                        .map((tpl) => (
                          <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                        ))}
                    </select>
                  </div>
                ))}
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" className="btn-outline-gold" onClick={() => setGenerateDialog(false)}>{t("diplomas.cancel")}</button>
                <button className="btn-primary-gold flex items-center gap-2" onClick={generateDiplomas} disabled={generating} data-testid="diploma-gen-submit">
                  <Award size={14} /> {generating ? "..." : (t("diplomas.generate"))}
                </button>
              </div>
              <p className="kv-label" style={{ fontSize: "0.7rem", marginTop: 4 }}>
                {t("diplomas.pdfs_are_saved_automatically_to")}
              </p>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <DiplomaEditor
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={(t) => {
            setEditing(t);
            load();
          }}
          en={en}
        />
      )}
    </div>
  );
}

/** Visual editor: image background + draggable text fields placed at normalized [0..1] coords. */
function DiplomaEditor({ template, onClose, onSaved, en }) {
  const { t } = useTranslation();
  const [fields, setFields] = useState(template.fields || []);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const imgWrapRef = useRef(null);
  const draggingRef = useRef(null);

  useEffect(() => { setFields(template.fields || []); }, [template.id]);

  const addField = (type = "text") => {
    if (type === "text") {
      setFields((prev) => [...prev, emptyField("member_name", "text")]);
    } else {
      setFields((prev) => [...prev, emptyField(null, type)]);
    }
    setSelected(fields.length);
  };

  const onMouseDown = (idx, e) => {
    e.preventDefault();
    setSelected(idx);
    draggingRef.current = { idx };
  };

  const onMouseMove = (e) => {
    const drag = draggingRef.current;
    if (!drag || !imgWrapRef.current) return;
    const rect = imgWrapRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setFields((prev) => prev.map((f, i) => i === drag.idx ? { ...f, x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) } : f));
  };

  const onMouseUp = () => { draggingRef.current = null; };

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const updateField = (idx, patch) => {
    setFields((prev) => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
  };

  const removeField = (idx) => {
    setFields((prev) => prev.filter((_, i) => i !== idx));
    if (selected === idx) setSelected(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/diplomas/templates/${template.id}`, { fields });
      toast.success(t("diplomas.saved"));
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const labelFor = (key) => {
    const f = FIELD_KEYS.find((x) => x.key === key);
    return f ? t(`diplomas.field_${f.key}`) : key;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content gold-card"
        style={{ maxWidth: 1180, width: "96vw", maxHeight: "94vh", padding: "1rem", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="diploma-editor"
      >
        <div className="flex items-center justify-between mb-2">
          <h2 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.16em" }}>
            {t("diplomas.configure_template")} — {template.name}
          </h2>
          <button onClick={onClose} className="text-gray-500" data-testid="diploma-editor-close"><X size={20} /></button>
        </div>
        <div style={{ display: "flex", gap: 14, flex: 1, minHeight: 0 }}>
          <div ref={imgWrapRef} style={{ flex: 2, position: "relative", border: "1px solid #ead8a8", background: "#fdfcf6", overflow: "hidden", userSelect: "none" }}>
            <img
              src={`${API}/diplomas/templates/${template.id}/image${authQuery()}`}
              alt={template.name}
              draggable={false}
              style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", pointerEvents: "none" }}
            />
            {fields.map((f, idx) => {
              const ftype = f.type || "text";
              const sourceLocalized = f.source ? t(`memberProfile.lodgeOffice.${f.source}`, f.source) : "";
              const labelText = ftype === "stamp"
                ? (t("diplomas.stamp"))
                : ftype === "signature"
                ? t("diplomas.sig_label_with_source", { source: sourceLocalized || t("memberProfile.lodgeOffice.Maestru Venerabil", "Maestru Venerabil") })
                : ftype === "photo"
                ? (t("diplomas.photo"))
                : labelFor(f.key);
              return (
                <div
                  key={idx}
                  onMouseDown={(e) => onMouseDown(idx, e)}
                  style={{
                    position: "absolute",
                    left: `${f.x * 100}%`,
                    top: `${f.y * 100}%`,
                    transform: "translate(-50%, -50%)",
                    padding: "2px 8px",
                    background: selected === idx ? "rgba(201,168,76,0.95)" : "rgba(13,27,42,0.85)",
                    color: selected === idx ? "#0d1b2a" : "#e8c97a",
                    fontFamily: "Cinzel, serif",
                    fontSize: `${Math.max(10, Math.min(18, ftype === "text" ? (f.font_size || 16) : 12))}px`,
                    border: ftype === "text" ? "1px dashed #c9a84c" : "2px solid #c9a84c",
                    cursor: "move",
                    whiteSpace: "nowrap",
                    letterSpacing: "0.05em",
                  }}
                  data-testid={`diploma-field-${idx}`}
                >
                  {labelText}
                </div>
              );
            })}
            <div style={{ position: "absolute", bottom: 6, right: 8, color: "#9a7530", fontSize: "0.7rem", fontStyle: "italic", background: "rgba(255,255,255,0.7)", padding: "2px 6px" }}>
              {t("diplomas.drag_fields_to_position_them")}
            </div>
          </div>
          <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
            <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
              <span className="kv-label">{t("diplomas.fields")}</span>
              <div className="flex gap-1 flex-wrap">
                <button className="btn-outline-gold flex items-center gap-1" onClick={() => addField("text")} data-testid="diploma-add-field">
                  <Plus size={12} /> {t("diplomas.text")}
                </button>
                <button className="btn-outline-gold flex items-center gap-1" onClick={() => addField("stamp")} data-testid="diploma-add-stamp">
                  <Plus size={12} /> {t("diplomas.stamp_2")}
                </button>
                <button className="btn-outline-gold flex items-center gap-1" onClick={() => addField("signature")} data-testid="diploma-add-signature">
                  <Plus size={12} /> {t("diplomas.signature")}
                </button>
                <button className="btn-outline-gold flex items-center gap-1" onClick={() => addField("photo")} data-testid="diploma-add-photo">
                  <Plus size={12} /> {t("diplomas.photo_2")}
                </button>
              </div>
            </div>
            {fields.length === 0 && (
              <p style={{ fontStyle: "italic", color: "#9a7530", fontSize: "0.85rem" }}>
                {t("diplomas.no_fields_yet_click_add")}
              </p>
            )}
            {fields.map((f, idx) => {
              const fieldType = f.type || "text";
              const isImageField = fieldType !== "text";
              return (
                <div key={idx} className="gold-card" style={{ padding: "0.7rem", marginBottom: 8, background: selected === idx ? "#fdf4e0" : undefined }} data-testid={`diploma-field-row-${idx}`}>
                  <div className="flex justify-between items-center mb-2">
                    <select
                      className="input-lodge"
                      style={{ flex: 1, marginRight: 6 }}
                      value={fieldType}
                      onChange={(e) => {
                        const t2 = e.target.value;
                        updateField(idx, {
                          type: t2,
                          // sensible defaults when switching type
                          key: t2 === "text" ? (f.key === "__custom" ? "__custom" : "member_name") : null,
                          source: t2 === "signature" ? (f.source || "Maestru Venerabil") : null,
                          font_size: t2 === "text" ? 22 : 110,
                        });
                      }}
                      data-testid={`diploma-field-type-${idx}`}
                    >
                      {FIELD_TYPES.map((ft) => (
                        <option key={ft.type} value={ft.type}>
                          {t(`diplomas.type_${ft.type}`)}
                        </option>
                      ))}
                    </select>
                    <button className="btn-danger" onClick={() => removeField(idx)} data-testid={`diploma-remove-field-${idx}`}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                  {fieldType === "text" && (
                    <div style={{ marginBottom: 8 }}>
                      <label className="label-lodge">{t("diplomas.data_field")}</label>
                      <select className="input-lodge" value={f.key} onChange={(e) => updateField(idx, { key: e.target.value })}>
                        {FIELD_KEYS.map((fk) => <option key={fk.key} value={fk.key}>{t(`diplomas.field_${fk.key}`)}</option>)}
                      </select>
                    </div>
                  )}
                  {fieldType === "signature" && (
                    <div style={{ marginBottom: 8 }}>
                      <label className="label-lodge">{t("diplomas.officer_role")}</label>
                      <select
                        className="input-lodge"
                        value={f.source || "Maestru Venerabil"}
                        onChange={(e) => updateField(idx, { source: e.target.value })}
                        data-testid={`diploma-officer-source-${idx}`}
                      >
                        {OFFICER_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {t(`memberProfile.lodgeOffice.${r}`, r)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {f.key === "__custom" && fieldType === "text" ? (
                    <div style={{ marginBottom: 8 }}>
                      <label className="label-lodge">{t("diplomas.custom_text")}</label>
                      <input
                        className="input-lodge"
                        placeholder={t("diplomas.e_g_in_lumea_masonilor")}
                        value={f.default_value || ""}
                        onChange={(e) => updateField(idx, { default_value: e.target.value })}
                        data-testid={`diploma-custom-text-${idx}`}
                      />
                    </div>
                  ) : null}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="label-lodge">
                        {isImageField ? (t("diplomas.width_pt_4cm_110")) : (t("diplomas.font_size"))}
                      </label>
                      <input type="number" min={isImageField ? 28 : 8} max={isImageField ? 360 : 120} className="input-lodge" value={f.font_size || (isImageField ? 110 : 18)} onChange={(e) => updateField(idx, { font_size: parseInt(e.target.value, 10) || (isImageField ? 110 : 18) })} />
                    </div>
                    {!isImageField && (
                      <>
                        <div>
                          <label className="label-lodge">{t("diplomas.style")}</label>
                          <select className="input-lodge" value={f.font || "regular"} onChange={(e) => updateField(idx, { font: e.target.value })}>
                            <option value="regular">{t("diplomas.regular")}</option>
                            <option value="bold">Bold</option>
                            <option value="italic">Italic</option>
                          </select>
                        </div>
                        <div>
                          <label className="label-lodge">{t("diplomas.align")}</label>
                          <select className="input-lodge" value={f.align || "center"} onChange={(e) => updateField(idx, { align: e.target.value })}>
                            <option value="left">{t("diplomas.left")}</option>
                            <option value="center">{t("diplomas.center")}</option>
                            <option value="right">{t("diplomas.right")}</option>
                          </select>
                        </div>
                        <div>
                          <label className="label-lodge">{t("diplomas.color")}</label>
                          <input type="color" className="input-lodge" value={f.color || "#0d1b2a"} onChange={(e) => updateField(idx, { color: e.target.value })} />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="kv-label" style={{ marginTop: 4, fontSize: "0.65rem" }}>
                    X {(f.x * 100).toFixed(1)}% · Y {(f.y * 100).toFixed(1)}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button className="btn-outline-gold" onClick={onClose}>{t("diplomas.close")}</button>
          <button className="btn-primary-gold flex items-center gap-2" onClick={save} disabled={busy} data-testid="diploma-editor-save">
            <Save size={14} /> {busy ? "..." : (t("diplomas.save"))}
          </button>
        </div>
      </div>
    </div>
  );
}
